import type { AlertType } from '@odin-labs/components';
import { isApolloError } from 'utils/error';
import {
  SiteControllerSyncStatus,
  SiteDeviceFailure,
  SiteDeviceRefusal,
  SiteDeviceResultKind,
  UserErrorCode,
} from 'apollo/generated/client-operations';

/** What the screen says about a site device command's answer, and whether it must reload first. */
export type SiteDeviceOutcome = {
  type: AlertType;
  title: string;
  message: string;
  /** The answer applied nothing and the screen's state may be stale: reload before another submit. */
  reload: boolean;
  /** No answer came: whether it applied is for the reloaded site to say. */
  lost?: boolean;
  /** Message after a successful refresh, without an in-progress claim. */
  reloadedMessage?: string;
  refusal?: SiteDeviceRefusal;
};

type Payload = {
  kind: SiteDeviceResultKind;
  runId?: string | null;
  refusal?: SiteDeviceRefusal | null;
  failure?: SiteDeviceFailure | null;
};

/** Each refusal in the user's words. None names another site. */
export const refusalMessages: Record<SiteDeviceRefusal, string> = {
  [SiteDeviceRefusal.ConnectorDisabled]:
    'This site has no enabled Avigilon Access connector.',
  [SiteDeviceRefusal.SerialRequired]: 'Enter the controller’s serial number.',
  [SiteDeviceRefusal.NotFound]:
    'Avigilon has no controller with this serial number.',
  [SiteDeviceRefusal.AmbiguousSerial]:
    'Avigilon has more than one controller with this serial number. Check Avigilon and correct the duplicate first.',
  [SiteDeviceRefusal.AlreadyAssigned]:
    'This controller is already assigned. Check Avigilon to determine its current assignment.',
  [SiteDeviceRefusal.ReaderConflict]:
    'A reader on this controller is registered on another site. Resolve that in Avigilon first.',
  [SiteDeviceRefusal.AssignmentChanged]:
    'This controller’s assignment changed. Reload to see the current state.',
  [SiteDeviceRefusal.PublishedPlacement]:
    'A reader on this controller is placed in the published layout. Unplace it and publish before removing the controller.',
  [SiteDeviceRefusal.AssociationUnresolved]:
    'A reader on this site has no recorded controller. Check the removal details: sync the controller shown for the reader, or ask Support if none is shown.',
};

export const failureMessages: Record<SiteDeviceFailure, string> = {
  [SiteDeviceFailure.ProviderUnavailable]:
    'Avigilon could not be reached. Nothing changed; try again.',
  [SiteDeviceFailure.IncompleteSnapshot]:
    'Avigilon could not list every port and reader of the controller. Nothing changed; try again.',
  [SiteDeviceFailure.SerialLookupUnsupported]:
    'Avigilon could not look up the controller by serial number. Ask Support.',
};

const withSupportReference = (message: string, payload: Payload): string =>
  /ask Support/i.test(message) && payload.runId
    ? `${message} Reference: ${payload.runId}.`
    : message;

const notApplied = (verb: string, payload: Payload): SiteDeviceOutcome => {
  if (payload.kind === SiteDeviceResultKind.Superseded)
    return {
      type: 'warn',
      title: `Nothing ${verb}`,
      message:
        'Another change to this controller landed meanwhile. Reloading the current state.',
      reload: true,
      reloadedMessage:
        'Another change to this controller landed meanwhile. The current state is shown.',
    };
  if (payload.kind === SiteDeviceResultKind.Refused && payload.refusal)
    return {
      type: 'warn',
      title: `Nothing ${verb}`,
      message: withSupportReference(
        refusalMessages[payload.refusal] ??
          'Odin refused this change. Reload the site before trying again.',
        payload,
      ),
      refusal: payload.refusal,
      reload:
        payload.refusal === SiteDeviceRefusal.AssignmentChanged ||
        payload.refusal === SiteDeviceRefusal.AlreadyAssigned,
    };
  return {
    type: 'danger',
    title: `Nothing ${verb}`,
    message: withSupportReference(
      payload.failure
        ? failureMessages[payload.failure] ??
            'Odin could not complete this. Try again.'
        : 'Odin could not complete this. Try again.',
      payload,
    ),
    reload: false,
  };
};

export const addOutcome = (
  payload: Payload & { readerIds?: string[] | null },
): SiteDeviceOutcome =>
  payload.kind === SiteDeviceResultKind.Applied
    ? {
        type: 'success',
        title: 'Controller added',
        message: `${payload.readerIds?.length ?? 0} reader(s) came with it.`,
        reload: false,
      }
    : notApplied('added', payload);

const syncMessages: Record<SiteControllerSyncStatus, [AlertType, string]> = {
  [SiteControllerSyncStatus.Changed]: [
    'success',
    'Avigilon reported changes; they are applied.',
  ],
  [SiteControllerSyncStatus.Unchanged]: [
    'success',
    'Avigilon reports what Odin has.',
  ],
  [SiteControllerSyncStatus.Missing]: [
    'warn',
    'Avigilon no longer has this controller. It is shown offline; everything recorded stays.',
  ],
};

export const syncOutcome = (
  payload: Payload & { status?: SiteControllerSyncStatus | null },
): SiteDeviceOutcome => {
  if (payload.kind === SiteDeviceResultKind.Applied) {
    const [type, message] = syncMessages[payload.status] ?? [
      'success',
      'The sync applied. The current state is shown.',
    ];
    return {
      type,
      title: 'Controller synced',
      message,
      reload: type !== 'success',
    };
  }
  const outcome = notApplied('synced', payload);
  return payload.kind === SiteDeviceResultKind.Failed
    ? {
        ...outcome,
        message: outcome.message.replace(
          'Nothing changed; try again.',
          'Inventory is preserved; try again.',
        ),
        reload: true,
      }
    : outcome;
};

export const removeOutcome = (
  payload: Payload & {
    closedReaderIds?: string[] | null;
    removedDraftPlacementIds?: string[] | null;
  },
): SiteDeviceOutcome => {
  if (payload.kind !== SiteDeviceResultKind.Applied)
    return notApplied('removed', payload);
  const readers = payload.closedReaderIds?.length ?? 0;
  const placements = payload.removedDraftPlacementIds?.length ?? 0;
  return {
    type: 'success',
    title: 'Controller removed',
    message: `${readers} reader(s) left the site with it${
      placements ? `, and ${placements} draft placement(s) were removed` : ''
    }.`,
    reload: false,
  };
};

/** The screen got no answer: whether the command applied is unknown until the site is read again. */
export const lostResponseOutcome = (verb: string): SiteDeviceOutcome => ({
  type: 'danger',
  title: `Odin did not answer the ${verb}`,
  message:
    'Whether it applied is unknown. Reloading the current state before another attempt.',
  reload: true,
  lost: true,
  reloadedMessage:
    'The current state is shown. Whether the request applied is still unknown.',
});

/** The refusals the API throws, in Odin's words; its input checks are shown as given. */
const thrownMessages: Record<string, string> = {
  [UserErrorCode.NotAuthorized]: 'You may not manage this site’s devices.',
  [UserErrorCode.NotAuthenticated]: 'Your session ended. Sign in again.',
};

/**
 * What a thrown error means. An answer from the API applied nothing (a
 * throw rolls the command back) and needs no reload: a refusal is told in
 * Odin's words, an input check as the API worded it, and anything else in
 * general words, so no server text reaches the screen. No answer at all
 * is a lost response.
 */
export const thrownOutcome = (
  /** What did not happen, as in "Nothing synced". */
  verb: string,
  /** What got no answer, as in "Odin did not answer the sync". */
  noun: string,
  error: unknown,
): SiteDeviceOutcome => {
  if (!isApolloError(error) || error.graphQLErrors.length === 0)
    return lostResponseOutcome(noun);
  const [{ message, extensions }] = error.graphQLErrors;
  const code = String(extensions?.code ?? '');
  return {
    type: 'danger',
    title: `Nothing ${verb}`,
    message:
      thrownMessages[code] ??
      (code === 'BAD_USER_INPUT'
        ? message
        : 'Odin could not complete this. Try again.'),
    reload: false,
  };
};

/** The reload after an answer failed: nothing can be trusted until one succeeds. */
export const reloadFailedOutcome = (): SiteDeviceOutcome => ({
  type: 'danger',
  title: 'Could not reload the site',
  message: 'Close and open the site again before another attempt.',
  reload: true,
});
